/**
 * Client calls for the team's saved email signatures, on the shared {@link opsClient}. Cross-shop,
 * so no shop grant; and no typed reason — the API audits these writes with a generated one (see
 * `EMAIL_SIGNATURE_AUDIT_REASONS` in the API for why).
 *
 * The generated client types `locale` as its own enum per DTO, so both directions go through a
 * guard here — never an assertion — and the rest of the dashboard only ever sees
 * {@link SavedSignature} and {@link SignatureForm}.
 */

import {
  CreateEmailSignatureDtoLocaleEnum,
  UpdateEmailSignatureDtoLocaleEnum,
  type EmailSignatureDto,
} from '../../../../libs/api-client/src/generated/models';
import { narrowTo } from './canned-responses';
import {
  savedLocale,
  signatureContent,
  type SavedSignature,
  type SignatureForm,
} from './email-signature/signature-library';
import { opsClient } from './ops-api';

/** The API's row as the page uses it. */
function fromDto(dto: EmailSignatureDto): SavedSignature {
  return {
    id: dto.id,
    name: dto.name,
    fullName: dto.fullName,
    jobTitle: dto.jobTitle,
    email: dto.email,
    phone: dto.phone,
    mobile: dto.mobile,
    photoUrl: dto.photoUrl,
    locale: savedLocale(dto.locale),
    showLogo: dto.showLogo,
    showLegal: dto.showLegal,
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

/** The outcome of a save. */
export interface SignatureWriteResult {
  readonly signature: SavedSignature;
  /** The audit row, or null (best-effort, or nothing changed). */
  readonly auditLogId: string | null;
}

/** Every saved signature, by name. */
export async function listEmailSignatures(): Promise<SavedSignature[]> {
  const { data } = await opsClient().listEmailSignatures();
  return data.data.map(fromDto);
}

/** Saves a new signature. */
export async function createEmailSignature(
  form: SignatureForm,
): Promise<SignatureWriteResult> {
  const content = signatureContent(form);
  const { data } = await opsClient().createEmailSignature({
    createEmailSignatureDto: {
      ...content,
      // The form's locale is already `de`/`en`; the fallback is what an impossible value becomes.
      locale:
        narrowTo(
          Object.values(CreateEmailSignatureDtoLocaleEnum),
          content.locale,
        ) ?? CreateEmailSignatureDtoLocaleEnum.De,
    },
  });
  return {
    signature: fromDto(data.signature),
    auditLogId: data.auditLogId ?? null,
  };
}

/**
 * Saves an edited signature against the version it was edited on. A newer version answers 409
 * (`isSignatureConflict`), which the page answers by offering to reload — never by retrying.
 */
export async function updateEmailSignature(
  id: string,
  form: SignatureForm,
  expectedUpdatedAt: string,
): Promise<SignatureWriteResult> {
  const content = signatureContent(form);
  const { data } = await opsClient().updateEmailSignature({
    id,
    updateEmailSignatureDto: {
      ...content,
      expectedUpdatedAt,
      locale:
        narrowTo(
          Object.values(UpdateEmailSignatureDtoLocaleEnum),
          content.locale,
        ) ?? UpdateEmailSignatureDtoLocaleEnum.De,
    },
  });
  return {
    signature: fromDto(data.signature),
    auditLogId: data.auditLogId ?? null,
  };
}

/** Deletes a signature. */
export async function deleteEmailSignature(id: string): Promise<void> {
  await opsClient().deleteEmailSignature({ id });
}
